/**
 * Portal layout registry (ADR-0010 P7 B+C) — the source of truth for which
 * layouts exist and, per layout, which **theme slots** (named colours) are
 * configurable. This makes branding *layout-aware*: a layout without a
 * sidebar simply doesn't declare the `sidebar*` slots, so the admin settings
 * UI (and preview) stop showing them.
 *
 * A slot maps to a CSS variable consumed by `PortalShell`.
 * Adding a knob = adding a slot here (no migration — `portal_config.theme`
 * is a free-form `{ slot: colour }` map).
 */

export interface ThemeSlot {
  /** Stable key stored in `portal_config.theme[key]`. */
  key: string;
  /** Human label shown in the settings UI. */
  label: string;
  /** CSS custom property the shell/preview read (e.g. `--portal-header`). */
  cssVar: string;
}

export interface PortalLayout {
  id: string;
  label: string;
  /** Whether this layout renders the page-nav sidebar at all. */
  hasSidebar: boolean;
  slots: ThemeSlot[];
}

/** Slots shared by the standard layout (header + sidebar + footer). */
const STANDARD_SLOTS: ThemeSlot[] = [
  { key: "header", label: "Header background", cssVar: "--portal-header" },
  { key: "header_text", label: "Header text", cssVar: "--portal-header-text" },
  { key: "sidebar", label: "Sidebar background", cssVar: "--portal-sidebar" },
  { key: "sidebar_text", label: "Sidebar text", cssVar: "--portal-sidebar-text" },
  { key: "sidebar_active", label: "Sidebar active item", cssVar: "--portal-sidebar-active" },
  { key: "accent", label: "Accent (links/buttons)", cssVar: "--portal-accent" },
  { key: "background", label: "Page background", cssVar: "--portal-bg" },
  { key: "text", label: "Body text", cssVar: "--portal-text" },
  { key: "footer", label: "Footer background", cssVar: "--portal-footer" },
];

/** Slots for the "app" layout (rail + floating content card, NO header/footer)
 *  — a Tray-admin / Asana-style console: the rail is the only branded surface
 *  and the page lives in a rounded card on the page background. */
const APP_SLOTS: ThemeSlot[] = [
  // No `background` slot: in this layout the page void IS the sidebar plane, so
  // the shell derives it from `sidebar` (a separate page background would just
  // be confusing — see PortalShell).
  { key: "sidebar", label: "Sidebar background", cssVar: "--portal-sidebar" },
  { key: "sidebar_text", label: "Sidebar text", cssVar: "--portal-sidebar-text" },
  { key: "sidebar_active", label: "Sidebar active item", cssVar: "--portal-sidebar-active" },
  { key: "accent", label: "Accent (links/buttons)", cssVar: "--portal-accent" },
  { key: "text", label: "Body text", cssVar: "--portal-text" },
];

export const PORTAL_LAYOUTS: PortalLayout[] = [
  {
    id: "standard",
    label: "Standard (header + sidebar + footer)",
    hasSidebar: true,
    slots: STANDARD_SLOTS,
  },
  {
    id: "app",
    label: "App (collapsible rail + floating content, no header)",
    hasSidebar: true,
    slots: APP_SLOTS,
  },
];

export function getLayout(id: string | undefined): PortalLayout {
  return PORTAL_LAYOUTS.find((l) => l.id === id) ?? PORTAL_LAYOUTS[0];
}

/**
 * Build the CSS-variable style object for a theme slot map against a layout:
 * only slots the layout declares (and that have a colour) are emitted, so a
 * stray key from another layout never leaks into the rendered theme.
 */
export function themeStyle(
  layoutId: string | undefined,
  theme: Record<string, string> | undefined,
): React.CSSProperties {
  const out: Record<string, string> = {};
  if (theme) {
    for (const slot of getLayout(layoutId).slots) {
      const v = theme[slot.key];
      if (v) out[slot.cssVar] = v;
    }
  }
  return out as React.CSSProperties;
}
